import { Box, Stack } from "@chakra-ui/react";
import {
  CAMPAIGNS_DATA_FRONT,
  CAMPAIGNS_STATE_VALUE_PUBLISHED,
  CAMPAIGNS_TYPE_VALUE_THEMATIC,
} from "@raiden/library-ui/constants/campaigns";
import SearchInfiniteProvider from "@raiden/library-ui/contexts/SearchInfinite/SearchInfiniteProvider";
import { useSearchInfinite } from "@raiden/library-ui/contexts/SearchInfinite/useSearchInfinite";
import generateApiUrl from "@raiden/library-ui/libraries/utils/generateApiUrl";
import generateFrontPath from "@raiden/library-ui/libraries/utils/generateFrontPath";
import { useIntl } from "react-intl";
import { CenteredContent } from "../../../components/CenteredContent";
import { PageHeaderPublic } from "../../../components/PageHeaderPublic";
import { PageLayoutPublic } from "../../../components/PageLayoutPublic";
import { Filters } from "./Filters";
import { Results } from "./Results";

/**
 * @param {object} params
 * @param {CampaignSearchFormValues} params.fields
 * @param {number} params.key
 * @param {import("@raiden/library-ui/types/Campaign").CampaignType} params.campaignType
 * @returns {string}
 */
export function generateCampaignsSearchUrl({ fields, key, campaignType }) {
  return generateApiUrl({
    id: "@campaigns.viewAny",
    query: {
      ...fields,
      category_id: fields.categoryId !== "" ? [fields.categoryId] : [],
      type: campaignType,
      state: [CAMPAIGNS_STATE_VALUE_PUBLISHED],
      per_page: campaignType === CAMPAIGNS_TYPE_VALUE_THEMATIC ? 24 : 32,
      page: fields.page + key,
      ...(campaignType === CAMPAIGNS_TYPE_VALUE_THEMATIC && {
        is_homepage: true,
      }),
      scope: "public",
      fields: ["campaigns.cover"],
    },
  });
}

/**
 * @typedef {object} CampaignSearchFormValues
 * @property {string} term
 * @property {string} sort
 * @property {string} categoryId
 * @property {number} page
 */

/**
 * @type {CampaignSearchFormValues}
 */
export const DEFAULT_VALUES = {
  term: "",
  sort: "",
  categoryId: "",
  page: 1,
};

/**
 * @typedef {object} Props
 * @property {import("@raiden/library-ui/types/Campaign").CampaignType} campaignType
 * @property {import("swr").SWRResponse<import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Configuration").Configuration>>} [swrResponseConfiguration]
 * @property {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Campaign").Campaign[]>} [campaignSearchResponse]
 * @property {CampaignSearchFormValues} initialFields
 * @property {string} campaignUrl
 */
/**
 * @param {Props} props
 */
export function CampaignsSearch({
  campaignType,
  campaignUrl,
  swrResponseConfiguration,
  campaignSearchResponse,
  initialFields,
}) {
  const intl = useIntl();

  /** @type {import("@raiden/library-ui/contexts/SearchInfinite/SearchInfiniteProvider").SearchInfiniteContextValue<CampaignSearchFormValues>} */
  const searchInfinite = useSearchInfinite({
    defaultValues: DEFAULT_VALUES,
    initialFields,
    generateUrl: (fields, key) =>
      generateCampaignsSearchUrl({ fields, key, campaignType }),
    useRouterUrl: true,
    autoSubmit: true,
    keepPreviousData: true,
    fallbackData: campaignSearchResponse ? [campaignSearchResponse] : undefined,
  });

  const campaignsData = CAMPAIGNS_DATA_FRONT[campaignType];

  return (
    <PageLayoutPublic
      header={
        <PageHeaderPublic
          title={campaignsData && intl.formatMessage(campaignsData.label)}
          breadCrumbItems={[
            {
              title: intl.formatMessage({ defaultMessage: "Accueil" }),
              url: generateFrontPath({ id: "@front.home" }),
            },
            {
              title: campaignsData && intl.formatMessage(campaignsData.label),
            },
          ]}
        />
      }>
      <SearchInfiniteProvider {...searchInfinite}>
        <Stack spacing="1rem">
          <Filters
            campaignType={campaignType}
            swrResponseConfiguration={swrResponseConfiguration}
          />

          <Box px="1rem">
            <CenteredContent>
              <Results campaignType={campaignType} campaignUrl={campaignUrl} />
            </CenteredContent>
          </Box>
        </Stack>
      </SearchInfiniteProvider>
    </PageLayoutPublic>
  );
}
